/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import LoadsService from '../../providers/Loads/Loads.service';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwCheckbox from '../TwCheckboxes/TwCheckboxes';
import TwTooltip from '../TwTooltip/TwTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DetailLoadPhoto,
  LoadClientType,
  LoadSavePDFParams
} from '../../redux/api/load/load.model';
import useNotification from '../../providers/Notifications/Notifications.provider';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import './TwSavePDF.scss';
import TwSelectableDocsPhotos from '../TwSelectableDocsPhotos/TwSelectableDocsPhotos.';
import FileUtils from '../../utils/fileUtils';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { useGetOrderByIdQuery, useSaveOrderAsPDFMutation } from '../../redux/api/orders/ordersApi';
import {
  CLIENT_TYPE,
  Order,
  OrderAttachment,
  SaveOrderAsPDFParams
} from '../../redux/api/orders/orders.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';

export interface TwSavePDFProps {
  /**
   * When true dialog is open
   */
  open: boolean;
  /**
   * Order id
   */
  orderId: string;
  /**
   * Action to perform when dialog closes
   */
  onClose: () => void;
  /**
   * Action to perform when submit is successful
   */
  onSuccess: (pdf: Blob, readableId: string, clientName: string) => void;
}

const TwSavePDF: React.FC<TwSavePDFProps> = ({
  open,
  orderId,
  onClose,
  onSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const [photosToDisplay, setPhotosToDisplay] = useState<OrderAttachment[]>([]);
  const [documents, setDocuments] = useState<OrderAttachment[]>([]);

  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const [invoice, setInvoice] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { addNotification } = useNotification();

  const [saveOrderAsPDF] = useSaveOrderAsPDFMutation();

  const { data: order } = useGetOrderByIdQuery(
    { orderId },
    {
      skip: !orderId || !open,
      refetchOnMountOrArgChange: true // to ensure that I'm getting the latest order data (e.g., with new invoice doc)
    }
  );

  const savePDF = async (): Promise<void> => {
    setLoading(true);

    const params: SaveOrderAsPDFParams = {
      orderId,
      attachments: [...selectedDocs, ...selectedPhotos],
      include_invoice: !!order?.invoice && invoice
    };

    try {
      const response = await saveOrderAsPDF(params).unwrap();

      if (response) {
        const readableId = order?.readable_id.toString() || '';
        const clientName =
          order?.primary_client === CLIENT_TYPE.SHIPPER
            ? order?.shipper?.name
            : order?.broker?.name;
        onSuccess(response, readableId, clientName || '');
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      logRocketTrack('Document Management', {
        action: 'Save load info as PDF',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'There was an error saving PDF. Please try again.',
        variant: 'error'
      });
      setLoading(false);
    }
  };

  const handleClose = (): void => {
    onClose();
    setPhotosToDisplay([]);
    setSelectedPhotos([]);
    setSelectedDocs([]);
    setDocuments([]);
  };

  useEffect(() => {
    if (!orderId) return;

    setLoading(true);
    setInvoice(!!order?.invoice ? true : false);
  }, [order]);

  useEffect(() => {
    if (open && order) {
      const onlyDocs = order.order_attachments.filter((docs) => {
        return !docs.attachment.file_type.includes('image');
      });

      if (onlyDocs) {
        setDocuments(onlyDocs);
      }
    }
    if (!open) {
      setDocuments([]);
      setSelectedPhotos([]);
      setSelectedDocs([]);
      setPhotosToDisplay([]);
    }
  }, [open, order]);

  useEffect(() => {
    if (!order || !order.order_attachments) return;

    (async (): Promise<void> => {
      try {
        const onlyPhotos =
          order.order_attachments.filter((docs) => {
            return docs.attachment.file_type.includes('image');
          }) || [];

        const newPhotosToDisplay: OrderAttachment[] = await FileUtils.getPhotosInBatch(
          onlyPhotos,
          5
        );
        setPhotosToDisplay(newPhotosToDisplay);
        setLoading(false);
      } catch (e) {
        console.error(e);
        logRocketTrack('Load Management', {
          action: 'Save load info as PDF',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({ message: 'There was an error loading photos', variant: 'error' });
        setLoading(false);
      }
    })();
  }, [order?.order_attachments]);

  useEffect(() => {
    if (selectedDocs.length === 0 && selectedPhotos.length === 0 && !invoice) {
      setDisableSubmit(true);
    } else {
      setDisableSubmit(false);
    }
  }, [selectedDocs, selectedPhotos, invoice]);

  return (
    <TwDialog
      open={open}
      onClickClose={handleClose}
      size="sm"
      title={`Save order${order?.readable_id ? ` #${order?.readable_id} as PDF` : ' as PDF'}`}
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Cancel"
          action={handleClose}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.primary}
          disabled={disableSubmit}
          label="Save PDF"
          action={savePDF}
        />
      ]}
    >
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: 10000,
          width: '100%'
        }}
        open={loading}
      >
        <CircularProgress role={'alert'} />
      </Backdrop>
      <div className="tid-save-pdf__wrapper">
        {order && !!order.invoice && (
          <div className="tid-save-pdf__load-invoice">
            <TwCheckbox
              name="includeInvoice"
              label="Include invoice"
              checked={invoice}
              onInputChange={(): void => {
                setInvoice((invoice) => !invoice);
              }}
            />
            <TwTooltip title="This is the invoice of the order" placement="right" arrow>
              <InfoOutlinedIcon sx={{ height: '16px', color: '#7e858e' }} />
            </TwTooltip>
          </div>
        )}

        {order && (
          <TwSelectableDocsPhotos
            documents={documents}
            photos={photosToDisplay}
            onSelectDocuments={setSelectedDocs}
            onSelectPhotos={setSelectedPhotos}
            disableEncryptedDocuments={true}
          />
        )}
      </div>
    </TwDialog>
  );
};

export default TwSavePDF;
